import type { DiagnosticAnswers, DiagnosticIdentityProvider, DiagnosticResult, DiagnosticView } from '@shared/prospect-diagnostic';
import type { DiagnosticDraft } from '@/lib/diagnostic-steps';
import { supabase, supabaseUrl } from '@/lib/supabase';

/**
 * Calls to the public `lead-diagnostic` function (online diagnostic, P1). No Domino session:
 * the anon key reaches the function, which answers `{ ok: false, code, message }` on refusals.
 * The draft and the diagnostic token live in the browser (they must survive the sign-in
 * redirect); storage can fail, and the page still works without it.
 */

export type LeadResult<T> = ({ ok: true } & T) | { ok: false; code: string; message: string };

const call = async <T,>(body: Record<string, unknown>, accessToken?: string): Promise<LeadResult<T>> => {
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;
  let response: Response;
  try {
    response = await fetch(`${supabaseUrl}/functions/v1/lead-diagnostic`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', apikey: anonKey, Authorization: `Bearer ${accessToken ?? anonKey}` },
      body: JSON.stringify(body),
    });
  } catch {
    return { ok: false, code: 'network', message: 'Connexion impossible. Vérifiez votre réseau.' };
  }
  const payload = await response.json().catch(() => null) as LeadResult<T> | null;
  return payload ?? { ok: false, code: 'server', message: 'Une erreur est survenue. Réessayez dans un instant.' };
};

export const readLeadConfig = () => call<{ providers: DiagnosticIdentityProvider[] }>({ action: 'config' });
export const sendLeadCode = (email: string, turnstileToken: string) => call<object>({ action: 'send_code', email, turnstileToken });
export const verifyLeadCode = (email: string, code: string, answers: DiagnosticAnswers) =>
  call<{ token: string; diagnostic: DiagnosticView }>({ action: 'verify_code', email, code, answers });
export const startLeadOAuth = (provider: DiagnosticIdentityProvider, turnstileToken: string) =>
  call<{ url: string }>({ action: 'oauth_start', provider, turnstileToken });
export const finishLeadOAuth = (provider: DiagnosticIdentityProvider, code: string, state: string, answers: DiagnosticAnswers) =>
  call<{ token: string; diagnostic: DiagnosticView }>({ action: 'oauth_finish', provider, code, state, answers });
export const readLeadDiagnostic = (token: string) => call<{ diagnostic: DiagnosticView }>({ action: 'read', token });
export const generateLeadDiagnostic = (token: string) => call<{ diagnostic: DiagnosticView }>({ action: 'generate', token });
export const emailLeadDiagnostic = (token: string) => call<{ sentTo: string }>({ action: 'email_me', token });
/** 03/11: tells Benoit a visitor answered the first question (the site); never blocks the visitor. */
export const announceLeadStart = (website: string, source: string | undefined, turnstileToken: string) =>
  call<object>({ action: 'site_entered', website, source, turnstileToken });

/** Once per site and browser session: going back and forth on the first screen sends one alert. */
const STARTED_KEY = 'domino.diagnostic.started';
export const alreadyAnnounced = (domain: string): boolean => {
  try { return window.sessionStorage.getItem(STARTED_KEY) === domain; } catch { return false; }
};
export const markAnnounced = (domain: string): void => {
  try { window.sessionStorage.setItem(STARTED_KEY, domain); } catch { /* storage unavailable: at worst a second alert */ }
};

/** Sandbox (Domino operators): the diagnostic for any answers, without identity, never saved. */
export const labLeadDiagnostic = async (answers: DiagnosticAnswers) => {
  const { data } = await supabase.auth.getSession();
  const accessToken = data.session?.access_token;
  if (!accessToken) return { ok: false as const, code: 'not_operator', message: 'Connectez-vous.' };
  return call<{ result: DiagnosticResult }>({ action: 'lab_generate', answers }, accessToken);
};

const DRAFT_KEY = 'domino.diagnostic.draft';
const TOKEN_KEY = 'domino.diagnostic.token';
const PROVIDER_KEY = 'domino.diagnostic.provider';

const read = (key: string): string | null => {
  try { return window.localStorage.getItem(key); } catch { return null; }
};
const write = (key: string, value: string | null) => {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch { /* private window: the page keeps its state in memory */ }
};

export const loadDraft = (): DiagnosticDraft | null => {
  const raw = read(DRAFT_KEY);
  if (!raw) return null;
  try { return JSON.parse(raw) as DiagnosticDraft; } catch { return null; }
};
export const saveDraft = (draft: DiagnosticDraft) => write(DRAFT_KEY, JSON.stringify(draft));
export const clearDraft = () => write(DRAFT_KEY, null);

export const loadToken = () => read(TOKEN_KEY);
export const saveToken = (token: string | null) => write(TOKEN_KEY, token);

export const savePendingProvider = (provider: DiagnosticIdentityProvider | null) => write(PROVIDER_KEY, provider);
export const loadPendingProvider = (): DiagnosticIdentityProvider | null => {
  const value = read(PROVIDER_KEY);
  return value === 'google' || value === 'microsoft' ? value : null;
};

export { DIAGNOSTIC_CONTACT_URL } from '@shared/prospect-diagnostic';
